import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Save } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { isOwner } from '@/lib/perms';
import { useConfirm } from '@/components/ConfirmDialog';

// Reusable proposal & invoice templates. These live in the Databases section
// (alongside the other reference data) rather than Settings, since they are
// content you maintain, not studio configuration. Owner-only, like the
// proposals and invoices they feed.
export default function DocumentTemplatesManager() {
  const { user } = useAuth();
  const [confirm, confirmDialog] = useConfirm();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get('/settings'); setSettings(r.data); }
    catch { toast.error('Failed to load templates'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = (path, value) => {
    setSettings(prev => {
      const next = { ...prev };
      const keys = path.split('.');
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) {
        obj[keys[i]] = { ...obj[keys[i]] };
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/settings', {
        proposal_templates: settings.proposal_templates,
        proposal_layout: settings.proposal_layout,
        invoice_template: settings.invoice_template,
      });
      toast.success('Templates saved');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const resetLayout = async () => {
    if (!(await confirm({
      title: 'Reset page layout?',
      message: 'New proposals will go back to the built-in letterhead layout. Saved proposals are unaffected.',
      confirmLabel: 'Reset',
    }))) return;
    update('proposal_layout', { boxes: [], page_count: 2 });
  };

  if (!isOwner(user))
    return <div className="glass-card rounded-2xl p-8 text-center text-sm text-[#6B7280]">Proposal and invoice templates are visible to the Owner only.</div>;

  if (loading || !settings)
    return <div className="flex items-center justify-center h-32"><div className="w-5 h-5 border-2 border-[#3A8C8C] border-t-transparent rounded-full animate-spin" /></div>;

  const layout = settings.proposal_layout;

  return (
    <div className="space-y-4" data-testid="document-templates-manager">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-xs text-[#6B7280]">The starting point for every new proposal and invoice.</p>
        <button onClick={save} disabled={saving} data-testid="doc-templates-save"
          className="flex items-center gap-2 bg-[#3A8C8C] text-white rounded-full px-5 py-2 text-xs font-medium hover:bg-[#2E7A7A] transition-colors disabled:opacity-50">
          <Save size={14} strokeWidth={1.5} /> {saving ? 'Saving…' : 'Save Templates'}
        </button>
      </div>

      {/* Proposal template */}
      <div className="glass-card rounded-2xl p-6 space-y-4">
        <div>
          <h3 className="section-heading text-sm font-semibold font-['Manrope']">Proposal Template</h3>
          <p className="text-xs text-[#6B7280] mb-1 mt-2">The default scope, terms and text a new lead's proposal starts from. One item per line.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label className="text-[10px] text-[#6B7280] uppercase">Default "Re:" line</Label>
            <Input value={settings.proposal_templates?.re_default || ''} onChange={e => update('proposal_templates.re_default', e.target.value)} className="mt-1 h-9 rounded-lg border-[#D4D0C8] text-sm" data-testid="tmpl-re" /></div>
          <div><Label className="text-[10px] text-[#6B7280] uppercase">Sign-off name</Label>
            <Input value={settings.proposal_templates?.sign_name || ''} onChange={e => update('proposal_templates.sign_name', e.target.value)} className="mt-1 h-9 rounded-lg border-[#D4D0C8] text-sm" data-testid="tmpl-sign" /></div>
        </div>
        <div><Label className="text-[10px] text-[#6B7280] uppercase">Scope of Work (one per line)</Label>
          <textarea value={(settings.proposal_templates?.default_scope_items || []).join('\n')} onChange={e => update('proposal_templates.default_scope_items', e.target.value.split('\n').filter(Boolean))}
            className="mt-1 w-full h-32 rounded-lg border border-[#D4D0C8] text-sm px-3 py-2 bg-white outline-none focus:border-[#3A8C8C]" data-testid="tmpl-scope" /></div>
        <div><Label className="text-[10px] text-[#6B7280] uppercase">Terms &amp; Conditions (one per line)</Label>
          <textarea value={(settings.proposal_templates?.default_terms || []).join('\n')} onChange={e => update('proposal_templates.default_terms', e.target.value.split('\n').filter(Boolean))}
            className="mt-1 w-full h-32 rounded-lg border border-[#D4D0C8] text-sm px-3 py-2 bg-white outline-none focus:border-[#3A8C8C]" data-testid="tmpl-terms" /></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label className="text-[10px] text-[#6B7280] uppercase">Quotation note</Label>
            <textarea value={settings.proposal_templates?.quotation_note || ''} onChange={e => update('proposal_templates.quotation_note', e.target.value)} className="mt-1 w-full h-20 rounded-lg border border-[#D4D0C8] text-sm px-3 py-2 bg-white outline-none focus:border-[#3A8C8C] resize-none" data-testid="tmpl-quote-note" /></div>
          <div><Label className="text-[10px] text-[#6B7280] uppercase">Closing note</Label>
            <textarea value={settings.proposal_templates?.closing_note || ''} onChange={e => update('proposal_templates.closing_note', e.target.value)} className="mt-1 w-full h-20 rounded-lg border border-[#D4D0C8] text-sm px-3 py-2 bg-white outline-none focus:border-[#3A8C8C] resize-none" data-testid="tmpl-closing" /></div>
        </div>

        <div className="rounded-lg border border-[#E8E6E1] bg-[#FAFAF8] px-3 py-2.5" data-testid="proposal-layout-status">
          <p className="text-[11px] font-medium text-[#2C2C2E]">Page layout</p>
          {layout?.boxes?.length ? (
            <p className="text-[11px] text-[#6B7280] mt-0.5">
              Custom layout saved — {layout.boxes.length} elements across {layout.page_count || 1} page(s). New proposals start from this.
              <button onClick={resetLayout} className="ml-2 text-[#C4727F] hover:underline" data-testid="proposal-layout-reset">Reset to built-in</button>
            </p>
          ) : (
            <p className="text-[11px] text-[#6B7280] mt-0.5">Using the built-in letterhead layout. Open any proposal, arrange it how you like, then use <span className="font-medium text-[#2C2C2E]">Save as Template</span> there to make it the default.</p>
          )}
        </div>
      </div>

      {/* Invoice template */}
      <div className="glass-card rounded-2xl p-6 space-y-4">
        <div>
          <h3 className="section-heading text-sm font-semibold font-['Manrope']">Invoice Template</h3>
          <p className="text-xs text-[#6B7280] mb-1 mt-2">The defaults a new invoice starts from. Bank details and letterhead contact live in Settings → Documents.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label className="text-[10px] text-[#6B7280] uppercase">Invoice number prefix</Label>
            <Input value={settings.invoice_template?.number_prefix || ''} onChange={e => update('invoice_template.number_prefix', e.target.value)} placeholder="INV" className="mt-1 h-9 rounded-lg border-[#D4D0C8] text-sm" data-testid="inv-tmpl-prefix" /></div>
          <div><Label className="text-[10px] text-[#6B7280] uppercase">Default line item description</Label>
            <Input value={settings.invoice_template?.default_description || ''} onChange={e => update('invoice_template.default_description', e.target.value)} className="mt-1 h-9 rounded-lg border-[#D4D0C8] text-sm" data-testid="inv-tmpl-desc" /></div>
        </div>
        <div><Label className="text-[10px] text-[#6B7280] uppercase">Closing note</Label>
          <textarea value={settings.invoice_template?.closing_note || ''} onChange={e => update('invoice_template.closing_note', e.target.value)} className="mt-1 w-full h-16 rounded-lg border border-[#D4D0C8] text-sm px-3 py-2 bg-white outline-none focus:border-[#3A8C8C] resize-none" data-testid="inv-tmpl-closing" /></div>
      </div>

      {confirmDialog}
    </div>
  );
}
